import { useState, useEffect } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { AdditionalPhotosPicker } from "./AdditionalPhotosPicker";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

/**
 * Lets an artist (or admin) backfill/edit the additional photos of an existing artwork.
 * Cover image is shown read-only (use Edit-artwork flow to swap it).
 */
export function ManagePhotosDialog({ artwork, open, onClose, onSaved }) {
  const [gallery, setGallery] = useState(artwork?.gallery || []);
  const [saving, setSaving] = useState(false);

  // Keep local state in sync when the dialog opens for a different artwork
  useEffect(() => {
    setGallery(artwork?.gallery || []);
  }, [artwork?.id, artwork?.gallery]);

  if (!artwork) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/artworks/${artwork.id}/gallery`, { gallery });
      toast.success(`Saved ${data.gallery.length} photo${data.gallery.length === 1 ? "" : "s"}`);
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose?.() : null)}>
      <DialogContent data-testid="manage-photos-dialog" className="rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl tracking-tighter font-light">
            Manage photos · {artwork.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#555]">
            Add detail shots, scale references or framing photos to help collectors picture the work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <p className="label-eyebrow text-[#555]">— Cover image</p>
            <div className="mt-3 flex items-center gap-3">
              <img
                src={resolveImg(artwork.image_url)}
                alt={artwork.title}
                className="w-24 h-24 object-cover border border-[#E5E0D8]"
              />
              <p className="text-xs text-[#555]">
                The cover stays primary across the site. To replace it, use the regular edit flow.
              </p>
            </div>
          </div>

          <div>
            <p className="label-eyebrow text-[#1E3A8A]">— Additional photos</p>
            <AdditionalPhotosPicker
              value={gallery}
              onChange={setGallery}
              testid="manage-gallery"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-none border-[#E5E0D8] hover:bg-[#F4EFE6]/40 label-eyebrow"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="manage-photos-save"
            onClick={save}
            disabled={saving}
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save photos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ManagePhotosDialog;
